import React, { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import { createEra, getEraByID, editEra } from '../../store/actions/era/era.action';
import CreateEra from '../../components/Eras/CreateEra';
import PageLoader from '../../components/Common/PageLoader';

const CreateErasContainer = () => {
  // Router hooks
  const params = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Redux state
  const isCreating = useSelector((state) => state.era.isCreating);
  const categories = useSelector((state) => state.auth.categories);

  // Local state
  const [mode, setMode] = useState('CREATE');
  const [eraToBeEdit, setEraToBeEdit] = useState(null);
  const [accessMode, setAccessMode] = useState('');
  const [accessDenied, setAccessDenied] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Set access based on categories
  const setAccess = useCallback(() => {
    try {
      const eraCategory = categories.find((category) => category.link === 'eras');
      if (eraCategory) {
        setAccessMode(eraCategory.mode);
        setAccessDenied(false);
      } else {
        throw new Error('Era category not found');
      }
    } catch (err) {
      setAccessDenied(true);
    }
  }, [categories]);

  // Fetch era for edit mode
  const getEra = useCallback(async (eraID) => {
    try {
      const era = await dispatch(getEraByID(eraID));
      setEraToBeEdit(era);
      setIsLoading(false);
    } catch (error) {
      console.error('Error fetching era:', error);
      navigate('/eras');
    }
  }, [dispatch, navigate]);

  // Handle create era
  const handleCreateEra = useCallback((eraData) => {
    return dispatch(createEra(eraData));
  }, [dispatch]);

  // Handle edit era
  const handleEditEra = useCallback((eraData) => {
    return dispatch(editEra(eraData));
  }, [dispatch]);

  // Initialize on mount
  useEffect(() => {
    // Determine mode from URL params
    const currentMode = params?.mode === 'edit' ? 'EDIT' : 'CREATE';
    setMode(currentMode);

    // Set access permissions
    setAccess();

    // Fetch era if in edit mode with ID
    if (params?.id && currentMode === 'EDIT') {
      getEra(params.id);
    } else {
      setIsLoading(false);
    }
  }, [params?.mode, params?.id, getEra, setAccess]);

  // Update access when categories change
  useEffect(() => {
    setAccess();
  }, [categories, setAccess]);

  // Loading state
  if (isLoading) {
    return <PageLoader />;
  }

  // Access denied state
  if (accessDenied) {
    return <div>This category is not accessible to the logged in user.</div>;
  }

  return (
    <section>
      <CreateEra
        accessMode={accessMode}
        onCreate={handleCreateEra}
        onEdit={handleEditEra}
        initialValues={eraToBeEdit}
        mode={mode}
        isCreating={isCreating}
      />
    </section>
  );
};

export default CreateErasContainer;
